/**
 * The approved deck's shared parts (Figma 13 Strategy, Zone plate · APPROVED):
 * the brand eyebrow, the Crake headline with its blue highlight box, keys,
 * source lines, the foot, number and date printing. Every template builds from
 * these, so the 42 templates speak one language.
 *
 * Dev label/value mode (D3): every value from the strategy is wrapped in
 * `V` (`data-v`), so the `<>` toggle colours it green and the deck's fixed copy
 * pink (slideTextSource.ts reads the same marks for Creo).
 */
import { Fragment, type CSSProperties, type ReactNode } from "react";
import type {
  StrategyDocument,
  StrategyNumber,
  StrategyPlatform,
  StrategySource,
} from "@/types/strategyDocument.type";
import type { StrategySlideMedia } from "@/types/strategy.type";
import ReactCountryFlag from "react-country-flag";
import PlatformGlyph from "../../components/PlatformGlyph";
import type { StrategySlide } from "../components/strategySlides";
import type { ChapterSpec } from "../deck/deckModel";

/** What every template receives. */
export interface TemplateContext {
  doc: StrategyDocument;
  slide: StrategySlide;
  /** The brand's name: every content slide's eyebrow and foot. */
  brand: string;
  periodStart?: string | null;
  periodEnd?: string | null;
  /** "2026 to 2027". */
  years: string;
  /** The slide's page number. */
  page: string;
  chapter?: ChapterSpec;
  /** "Chapter n of N" for openers and the close. */
  chapterOf?: { number: number; total: number; slides: number };
  /** The strategy row, for the record. */
  record?: { status: string; version: string; liveAt?: string | null; updatedAt?: string | null };
  /** A picture slot: the chosen media, or the team's empty well; null when nothing shows. */
  picture: (slot: string, label: string, opts?: { fallbackUrl?: string | null }) => ReactNode;
  media: (slot: string) => StrategySlideMedia | undefined;
  /** A document picture_url as a link: a library picture ("media:<id>") resolves to its fresh link, anything else passes through. */
  pictureUrl: (url?: string | null) => string | null;
  readOnly: boolean;
}

/* ── Values ────────────────────────────────────────────────────────────── */

/** A value from the strategy (green in dev label/value mode). */
export function V({ children, as: Tag = "span", className, style, dir }: { children: ReactNode; as?: "span" | "p" | "div" | "b" | "strong"; className?: string; style?: CSSProperties; dir?: "auto" }) {
  return <Tag data-v="" className={className} style={style} dir={dir}>{children}</Tag>;
}

/** The slide's own lines a strategist writes (headline, reading, caption). */
export const slideText = (ctx: TemplateContext, key = ctx.slide.key) => ctx.doc.slide_text?.[key] ?? {};

/* ── Numbers and dates ─────────────────────────────────────────────────── */

const trim = (n: number, digits: number) => {
  const fixed = n.toFixed(digits);
  return fixed.includes(".") ? fixed.replace(/\.?0+$/, "") : fixed;
};

/** 2580000 → "2.58M", 632000 → "632K", 2100000000 → "2.1B", 940 → "940", 4120 → "4,120". */
export function compact(value: number, opts: { lower?: boolean; digits?: number } = {}): string {
  const abs = Math.abs(value);
  const sign = value < 0 ? "-" : "";
  const unit = (s: string) => (opts.lower ? s.toLowerCase() : s);
  const sig = opts.digits ?? 3;
  const scaled = (n: number) => {
    const intDigits = Math.max(1, Math.floor(Math.log10(Math.max(n, 1))) + 1);
    return trim(n, Math.max(0, sig - intDigits));
  };
  if (abs >= 1e9) return `${sign}${scaled(abs / 1e9)}${unit("B")}`;
  if (abs >= 1e6) return `${sign}${scaled(abs / 1e6)}${unit("M")}`;
  if (abs >= 1e4) return `${sign}${scaled(abs / 1e3)}${unit("K")}`;
  return `${sign}${abs.toLocaleString("en-US", { maximumFractionDigits: 1 })}`;
}

/** A number record as the slide prints it ("4.5%", "+23%", "11.4M", "3.1×"); null when open. */
export function num(n: StrategyNumber | null | undefined, opts: { lower?: boolean } = {}): string | null {
  if (!n || n.value == null) return null;
  const v = n.value;
  const sign = n.signed && v > 0 ? "+" : "";
  switch (n.unit) {
    case "percent": return `${sign}${trim(v, 1)}%`;
    case "ratio": return `${sign}${trim(v, 1)}×`;
    case "per_1000":
    case "level":
    case "points": return `${sign}${trim(v, 1)}`;
    case "currency": return `${n.currency ?? ""} ${compact(v, opts)}`.trim();
    case "hours": return `${trim(v, 1)} hours`;
    case "seconds": return `${trim(v, 0)} seconds`;
    default: return `${sign}${compact(v, opts)}`;
  }
}

/** A plain number with a unit, for goals' targets ("450k", "4.5%", "25"). */
export function figure(value: number, unit?: string | null, opts: { lower?: boolean } = {}): string {
  return num({ value, unit: (unit ?? "count") as StrategyNumber["unit"] }, opts) ?? "";
}

function parse(value: string | null | undefined): Date | null {
  if (!value) return null;
  const date = new Date(value.length === 10 ? `${value}T00:00:00Z` : value);
  return Number.isNaN(date.getTime()) ? null : date;
}
const fmt = (options: Intl.DateTimeFormatOptions) => {
  const f = new Intl.DateTimeFormat("en-GB", { ...options, timeZone: "UTC" });
  return (value: string | null | undefined) => {
    const d = parse(value);
    return d ? f.format(d) : "";
  };
};
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
/** "12 Oct" (three letters, never "Sept"). */
export const dayShort = (value: string | null | undefined) => {
  const d = parse(value);
  return d ? `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]}` : "";
};
/** "Sep 2026". */
export const monthShortYear = (value: string | null | undefined) => {
  const d = parse(value);
  return d ? `${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}` : "";
};
/** "12 October". */
export const dayLong = fmt({ day: "numeric", month: "long" });
/** "12 October 2026". */
export const dayLongYear = fmt({ day: "numeric", month: "long", year: "numeric" });
/** "November 2026". */
export const monthYear = fmt({ month: "long", year: "numeric" });
/** "Oct". */
export const monthShort = (value: string | null | undefined) => {
  const d = parse(value);
  return d ? MONTHS[d.getUTCMonth()] : "";
};

/** "15 Jul to 12 Oct"; one date when they match. */
export function rangeShort(start?: string | null, end?: string | null): string {
  if (!start) return "";
  if (!end || start.slice(0, 10) === end.slice(0, 10)) return dayShort(start);
  return `${dayShort(start)} to ${dayShort(end)}`;
}

/** "15 July to 12 October". */
export function rangeLong(start?: string | null, end?: string | null): string {
  if (!start) return "";
  if (!end || start.slice(0, 10) === end.slice(0, 10)) return dayLong(start);
  return `${dayLong(start)} to ${dayLong(end)}`;
}

/* ── Words ─────────────────────────────────────────────────────────────── */

const WORDS = ["no", "one", "two", "three", "four", "five", "six", "seven", "eight", "nine", "ten", "eleven", "twelve"];
/** 3 → "three"; 13 and up → digits. */
export const countWord = (n: number) => WORDS[n] ?? String(n);
export const capFirst = (text: string) => text.replace(/^(\s*["“‘'(]?)(\p{Ll})/u, (_, lead: string, letter: string) => lead + letter.toUpperCase());
/** "A and B", "A, B and C". */
export const joinAnd = (items: string[]) => (items.length < 2 ? items.join("") : `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`);
/** A full stop when the text doesn't already end a sentence. */
export const sentence = (text: string) => {
  const t = text.trim();
  return !t || /[.!?…]["”’)]?$/.test(t) ? t : `${t}.`;
};

const COUNTRY_NAMES: Record<string, string> = { SA: "Saudi Arabia", AE: "the UAE", KW: "Kuwait", QA: "Qatar", BH: "Bahrain", OM: "Oman", EG: "Egypt", JO: "Jordan", LB: "Lebanon", MA: "Morocco", TR: "Turkey" };
/** "SA" → "Saudi Arabia". */
export const countryName = (code: string) => COUNTRY_NAMES[code.toUpperCase()] ?? code.toUpperCase();
/** A country's flag, drawn (Windows shows flag emoji as letters). */
export function Flag({ code, size = 14 }: { code: string; size?: number }) {
  if (code.length !== 2) return null;
  return <ReactCountryFlag countryCode={code.toUpperCase()} svg aria-label={countryName(code)} className="sv-flag" style={{ width: Math.round(size * 1.4), height: size }} />;
}

const PLATFORM_NAMES: Record<StrategyPlatform, string> = { INSTAGRAM: "Instagram", TIKTOK: "TikTok", YOUTUBE: "YouTube", SNAPCHAT: "Snapchat", FACEBOOK: "Facebook", X: "X", LINKEDIN: "LinkedIn" };
export const platformName = (p: string) => PLATFORM_NAMES[p as StrategyPlatform] ?? p;

/** A platform's mark at `size` px (the Library glyphs). */
export function Glyph({ platform, size = 14, className }: { platform: string; size?: number; className?: string }) {
  return (
    <span className={`sv-glyph${className ? ` ${className}` : ""}`} style={{ "--glyph": `${size}px` } as CSSProperties}>
      <PlatformGlyph platform={platform} className="size-full" />
    </span>
  );
}

/** "Fatafeat" → the competitor's name, "brand" → the brand's. */
export function accountName(doc: StrategyDocument, brand: string, account: string): string {
  if (account === "brand") return brand;
  return doc.competitors?.set.find((c) => c.id === account)?.name ?? account;
}

const PILLAR_COLOURS = ["#6a00ba", "#207cf5", "#453ed8", "#04e735", "#9a9a9a"];
const PILLAR_COLOURS_INK = ["#a96bd7", "#207cf5", "#6574e6", "#04e735", "#9a9a9a"];
/** A pillar's colour, by its place in the content chapter (lighter on ink). */
export function pillarColour(doc: StrategyDocument, pillarId: string | null | undefined, ink?: boolean): string {
  const palette = ink ? PILLAR_COLOURS_INK : PILLAR_COLOURS;
  const i = doc.content?.pillars.findIndex((p) => p.id === pillarId) ?? -1;
  return palette[Math.max(0, i) % palette.length];
}

/* ── Pieces of a slide ─────────────────────────────────────────────────── */

/** A slide's root: 1280 × 720, paper or ink. */
export function Slide({ children, ink, className, style }: { children: ReactNode; ink?: boolean; className?: string; style?: CSSProperties }) {
  return <div className={`sv${ink ? " sv-ink" : ""}${className ? ` ${className}` : ""}`} style={style}>{children}</div>;
}

/** "FATAFEAT  ·  THE BRIEF · PERSONA 1 OF 3". */
export function Eyebrow({ ctx, label, extra }: { ctx: TemplateContext; label?: string; extra?: ReactNode }) {
  const chapter = label ?? ctx.chapter?.eyebrow ?? "";
  return (
    <div className="sv-eyebrow">
      <V as="b">{ctx.brand}</V>
      <span>·  {chapter}{extra ? <> · {extra}</> : null}</span>
    </div>
  );
}

/**
 * The claim, in Crake: `*asterisks*` mark the highlighted words (the blue box,
 * Crake Bold), `\n` breaks a line. A strategist's headline is a value; a
 * template's fixed fallback is not.
 */
export function Headline({ text, fallback, size = 58, lineHeight, className, valueWords }: { text?: string | null; fallback?: string; size?: number; lineHeight?: number; className?: string; valueWords?: boolean }) {
  // A line break typed as backslash-n breaks the line; it never prints.
  const source = (text?.trim() ? text : fallback ?? "").replace(/\\+n/g, "\n");
  const isValue = !!text?.trim() || !!valueWords;
  const lines = source.split("\n");
  const body = (
    <div className={`sv-claim${className ? ` ${className}` : ""}`} style={{ "--claim-size": `${size}px`, "--claim-lh": `${lineHeight ?? Math.round(size * 1.12)}px` } as CSSProperties}>
      {lines.map((line, i) => (
        <div key={i} className="sv-claim-line" dir="auto">
          {line.split(/(\*[^*]+\*)/).filter(Boolean).map((part, j) =>
            part.startsWith("*") && part.endsWith("*")
              ? <mark key={j} className="sv-hl">{part.slice(1, -1)}</mark>
              : <span key={j}>{part}</span>,
          )}
        </div>
      ))}
    </div>
  );
  return isValue ? <V as="div">{body}</V> : body;
}

/** "Fatafeat · Social strategy 2026 to 2027 · The brief" … page. */
export function Foot({ ctx, label, page = ctx.page, extra, flag }: { ctx: TemplateContext; label?: string | null; page?: string; extra?: ReactNode; flag?: ReactNode }) {
  const chapter = label === undefined ? ctx.chapter?.foot : label;
  return (
    <div className="sv-foot">
      <span><V>{ctx.brand}</V> · Social strategy <V>{ctx.years}</V>{chapter ? <> · {chapter}</> : null}{extra}</span>
      {flag}
      <span className="sv-push" />
      <b>{page}</b>
    </div>
  );
}

/** A Barlow key ("THE BUSINESS OBJECTIVE"). */
export function Key({ children, className }: { children: ReactNode; className?: string }) {
  return <p className={`sv-key${className ? ` ${className}` : ""}`}>{children}</p>;
}

/** The source line under a figure: the diamond and the source, in red. */
export function SourceLine({ source, className, children }: { source?: StrategySource | null; className?: string; children?: ReactNode }) {
  const text = children ?? source?.label ?? (source?.kind === "open" ? source.what_would_settle_it : null);
  if (!text) return null;
  return (
    <p className={`sv-source${className ? ` ${className}` : ""}`}>
      <svg aria-hidden viewBox="0 0 10 10" width="10" height="10"><path d="M5 .9 9.1 5 5 9.1.9 5Z" fill="none" stroke="currentColor" strokeWidth="1.2" /></svg>
      <V>{text}</V>
    </p>
  );
}

/** An open value: what would settle it, in red. */
export function Open({ source, className }: { source?: StrategySource | null; className?: string }) {
  return <V className={`sv-open${className ? ` ${className}` : ""}`}>{source?.what_would_settle_it || "Open"}</V>;
}

/** A missing figure: an em dash, never "Open" (the line around it says what would settle it). */
export const NO_FIGURE = "—";

/** A figure, or the em dash when it is open or missing. */
export function FigureOr({ value, className }: { value?: ReactNode; className?: string }) {
  if (value == null || value === "") return <span className={className}>{NO_FIGURE}</span>;
  return <V className={className}>{value}</V>;
}

/** A value, or what would settle it when it is open. */
export function ValueOr({ value, source, className }: { value?: ReactNode; source?: StrategySource | null; className?: string }) {
  if (source?.kind === "open" || value == null || value === "") return <Open source={source} className={className} />;
  return <V className={className}>{value}</V>;
}

/** Joins nodes with a separator. */
export function joinNodes(nodes: ReactNode[], separator: ReactNode = " · "): ReactNode {
  return nodes.map((node, i) => <Fragment key={i}>{i ? separator : null}{node}</Fragment>);
}

/** A picture well that fills its box (the slot's media, the read's picture, or the team's empty well). */
export function Picture({ ctx, slot, label, url: stored, className }: { ctx: TemplateContext; slot?: string; label: string; url?: string | null; className?: string }) {
  const url = ctx.pictureUrl(stored);
  const chosen = slot ? ctx.media(slot) : undefined;
  if (slot && (chosen || !url)) {
    const node = ctx.picture(slot, label, { fallbackUrl: url });
    return <div className={`sv-pic${className ? ` ${className}` : ""}`}>{node}</div>;
  }
  return <div className={`sv-pic${className ? ` ${className}` : ""}`}>{url ? <img src={url} alt={label} loading="lazy" /> : null}</div>;
}
